import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Header } from "@/components/Header";
import { PROTOCOLS } from "@/lib/protocols/definitions";
import { getMyProtocols } from "@/lib/protocols.functions";
import { useLocale, useT } from "@/i18n/locale-context";
import { useJurisdiction } from "@/hooks/useJurisdiction";
import { getDisclaimerPack } from "@/lib/jurisdiction";
import { supabase } from "@/integrations/supabase/client";
import { usePremium } from "@/hooks/usePremium";
import { isProtocolUnlocked, isProBillingConfigured } from "@/lib/feature-flags";

export const Route = createFileRoute("/protocols/")({
  head: () => ({ meta: [{ title: "Suivi spécialisé — Glow Up Coach" }] }),
  component: ProtocolsIndex,
});

function ProtocolsIndex() {
  const t = useT();
  const { locale } = useLocale();
  const nav = useNavigate();
  const fetchMine = useServerFn(getMyProtocols);
  const { jurisdiction } = useJurisdiction();
  const pack = getDisclaimerPack(jurisdiction, locale);
  const { isPremium, isPro } = usePremium();
  const [upsellOpen, setUpsellOpen] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (!data.session) nav({ to: "/login" });
    });
  }, [nav]);

  const { data } = useQuery({
    queryKey: ["my-protocols"],
    queryFn: () => fetchMine(),
  });
  const sessions = data?.sessions ?? [];

  const coreProtocols = PROTOCOLS.filter((p) => p.tier === "core");
  const proProtocols = PROTOCOLS.filter((p) => p.tier === "pro");

  function renderCard(p: (typeof PROTOCOLS)[number]) {
    const active = sessions.find((s) => s.goal === p.goal && s.status === "active");
    const unlocked = isProtocolUnlocked(p, { isPremium, isPro });
    const showLock = !unlocked;
    // Pro cards: navigate to the detail page (preview allowed), only
    // starting the protocol requires the upsell. The detail page handles
    // the upsell card. Core-locked cards (no Premium) keep their modal.
    const handleClick = (e: React.MouseEvent) => {
      if (showLock && p.tier === "core") {
        e.preventDefault();
        setUpsellOpen(true);
      }
    };
    return (
      <Link
        key={p.goal}
        to="/protocols/$goal"
        params={{ goal: p.goal }}
        onClick={handleClick}
        aria-disabled={showLock}
        className={
          "block rounded-xl border border-foreground/10 p-5 transition-colors " +
          (showLock
            ? "opacity-70 hover:bg-foreground/[0.02] cursor-pointer"
            : "hover:bg-foreground/[0.03]")
        }
      >
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            {showLock && <span aria-hidden>🔒</span>}
            {p.title[locale]}
          </h2>
          <div className="flex flex-col items-end gap-1">
            {p.tier === "pro" && (
              <span className="text-[10px] uppercase tracking-wider bg-primary/20 text-primary rounded-full px-2 py-0.5">
                {t("protocols.badge.pro")}
              </span>
            )}
            {active && (
              <span className="text-[10px] uppercase tracking-wider bg-accent/20 text-accent-foreground rounded-full px-2 py-0.5">
                {t("protocols.badge.active")}
              </span>
            )}
          </div>
        </div>
        <p className="text-sm text-muted-foreground mt-2">{p.subtitle[locale]}</p>
        <p className="text-xs text-muted-foreground mt-3">{p.steps.length} {t("protocols.steps")}</p>
      </Link>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground" style={{ fontFamily: "var(--font-sans)" }}>
      <Header />
      <main className="max-w-5xl mx-auto px-6 py-12">
        <p className="text-xs uppercase tracking-widest text-muted-foreground">{t("protocols.kicker")}</p>
        <h1 className="text-4xl font-bold mt-2">{t("protocols.title")}</h1>
        <p className="mt-3 text-muted-foreground max-w-2xl">{t("protocols.subtitle")}</p>

        <div className="mt-6 rounded-lg border border-foreground/10 bg-foreground/[0.02] p-4 text-sm">
          <strong className="block mb-1">{t("disclaimer.banner.label")}</strong>
          {pack.banner} <span className="text-muted-foreground">· {pack.emergency}</span>
        </div>

        <section className="mt-10">
          <h2 className="text-xs uppercase tracking-widest text-muted-foreground mb-3">{t("protocols.section.core.title")}</h2>
          <div className="grid gap-4 md:grid-cols-2">
            {coreProtocols.map(renderCard)}
          </div>
        </section>

        <section className="mt-12">
          <div className="flex items-baseline justify-between gap-3 mb-3">
            <h2 className="text-xs uppercase tracking-widest text-muted-foreground">{t("protocols.section.pro.title")}</h2>
            {!isPro && (
              <button
                type="button"
                onClick={() => setUpsellOpen(true)}
                className="text-xs underline text-primary hover:no-underline"
              >
                {t("protocols.locked.cta")}
              </button>
            )}
          </div>
          <p className="text-sm text-muted-foreground mb-4 max-w-2xl">{t("protocols.section.pro.subtitle")}</p>
          <div className="grid gap-4 md:grid-cols-2">
            {proProtocols.map(renderCard)}
          </div>
        </section>

        {upsellOpen && (
          <div
            role="dialog"
            aria-modal="true"
            className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4"
            onClick={() => setUpsellOpen(false)}
          >
            <div
              className="max-w-md w-full rounded-xl border border-foreground/10 bg-card p-6 shadow-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <p className="text-xs uppercase tracking-widest text-primary">{t("protocols.badge.pro")}</p>
              <h3 className="text-xl font-semibold mt-1">{t("protocols.locked.title")}</h3>
              <p className="text-sm text-muted-foreground mt-2">{t("protocols.locked.desc")}</p>
              <p className="text-sm font-medium text-primary mt-2">{t("protocols.locked.trial")}</p>
              <div className="mt-4 rounded-md border border-foreground/10 bg-foreground/[0.03] p-3">
                <p className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">{t("protocols.locked.limits.title")}</p>
                <p className="text-xs text-muted-foreground leading-relaxed">{t("protocols.locked.limits.preflight")}</p>
              </div>
              <div className="mt-5 flex flex-col gap-2">
                {isProBillingConfigured() ? (
                  <Link
                    to="/checkout"
                    className="block w-full text-center rounded-md bg-primary text-primary-foreground px-4 py-2 font-medium"
                  >
                    {t("protocols.locked.cta")}
                  </Link>
                ) : (
                  <div className="rounded-md bg-foreground/[0.04] px-4 py-3 text-sm text-center">
                    {t("protocols.locked.coming_soon")}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => setUpsellOpen(false)}
                  className="text-sm text-muted-foreground hover:text-foreground"
                >
                  {t("protocols.locked.dismiss")}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
